import * as React from "react";
import { getLocalDevIdentity } from "../config/localDev";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuthTokenMeta {
  token: string;
  expiresAtUtcMillis: number;
}

export function isAuthTokenExpired(
  token: AuthTokenMeta | null | undefined,
  now = Date.now()
): boolean {
  return token == null || token.expiresAtUtcMillis <= now;
}

export interface SelectedAccount {
  accountId: string;
  appId: string;
}

export interface AuthState {
  /** The short-lived access (star) token, if present. */
  accessToken: AuthTokenMeta | null;
  /** The long-lived refresh token, if present. */
  refreshToken: AuthTokenMeta | null;
  /** The email address associated with the authenticated user. */
  email: string | null;
  /** The account the user has selected within the session. */
  selectedAccount: SelectedAccount | null;
  /**
   * True once the provider has finished reading persisted state from
   * localStorage on mount. Components that need auth must wait for this.
   */
  isBootstrapped: boolean;
}

// ---------------------------------------------------------------------------
// localStorage keys (single source of truth)
// ---------------------------------------------------------------------------

export const LS_KEYS = {
  ACCESS_TOKEN: "auth.accessToken",
  REFRESH_TOKEN: "auth.refreshToken",
  EMAIL: "auth.email",
  SELECTED_ACCOUNT: "auth.selectedAccount",
} as const;

export const AUTH_STORAGE_RESET_MIGRATION_KEY = "auth.storageReset.rememberMeRefresh.v1";

const LEGACY_AUTH_STORAGE_KEYS = [
  "STARFORM_WEB_:startoken",
  "STARFORM_WEB_:refreshtoken",
  "STARFORM_WEB_:login-email",
  "STARFORM_WEB_:active-account",
  "STARFORM_WEB_:accounts",
  "STARFORM_WEB_:pin-code",
] as const;

// ---------------------------------------------------------------------------
// Context value shape
// ---------------------------------------------------------------------------

interface AuthContextValue {
  state: AuthState;
  setState: React.Dispatch<React.SetStateAction<AuthState>>;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = React.createContext<AuthContextValue | undefined>(undefined);
AuthContext.displayName = "AuthContext";

const EMPTY_AUTH_STATE: AuthState = {
  accessToken: null,
  refreshToken: null,
  email: null,
  selectedAccount: null,
  isBootstrapped: false,
};

// ---------------------------------------------------------------------------
// Initial state helper — reads from localStorage
// ---------------------------------------------------------------------------

function readLocalStorage(key: string): unknown {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function removeLocalStorage(key: string): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.removeItem(key); } catch { /* noop */ }
}

function writeLocalStorage(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(key, JSON.stringify(value)); } catch { /* noop */ }
}

function hasPersistedAuthStorage(): boolean {
  if (typeof window === "undefined") return false;
  const authKeys = [...Object.values(LS_KEYS), ...LEGACY_AUTH_STORAGE_KEYS];
  return authKeys.some((key) => window.localStorage.getItem(key) !== null);
}

function runOneTimeAuthStorageReset(): void {
  if (typeof window === "undefined") return;
  try {
    if (window.localStorage.getItem(AUTH_STORAGE_RESET_MIGRATION_KEY) === "true") return;

    if (hasPersistedAuthStorage()) {
      window.localStorage.clear();
    }

    window.localStorage.setItem(AUTH_STORAGE_RESET_MIGRATION_KEY, "true");
  } catch {
    // If storage is unavailable, leave auth empty rather than blocking render.
  }
}

function isAuthTokenMeta(value: unknown): value is AuthTokenMeta {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as AuthTokenMeta).token === "string" &&
    (value as AuthTokenMeta).token.length > 0 &&
    typeof (value as AuthTokenMeta).expiresAtUtcMillis === "number"
  );
}

function isSelectedAccount(value: unknown): value is SelectedAccount {
  return (
    typeof value === "object" &&
    value !== null &&
    typeof (value as SelectedAccount).accountId === "string" &&
    typeof (value as SelectedAccount).appId === "string"
  );
}

function buildLocalDevAuthState(): Omit<AuthState, "isBootstrapped"> | null {
  const localDevIdentity = getLocalDevIdentity();
  if (localDevIdentity === null) return null;

  const accessToken: AuthTokenMeta = {
    token: localDevIdentity.accessToken,
    expiresAtUtcMillis: localDevIdentity.expiresAtUtcMillis,
  };
  const refreshToken: AuthTokenMeta = {
    token: localDevIdentity.refreshToken,
    expiresAtUtcMillis: localDevIdentity.expiresAtUtcMillis,
  };
  const selectedAccount: SelectedAccount = {
    accountId: localDevIdentity.accountId,
    appId: localDevIdentity.appId,
  };

  writeLocalStorage(LS_KEYS.ACCESS_TOKEN, accessToken);
  writeLocalStorage(LS_KEYS.REFRESH_TOKEN, refreshToken);
  writeLocalStorage(LS_KEYS.EMAIL, localDevIdentity.email);
  writeLocalStorage(LS_KEYS.SELECTED_ACCOUNT, selectedAccount);

  return {
    accessToken,
    refreshToken,
    email: localDevIdentity.email,
    selectedAccount,
  };
}

function buildInitialState(): AuthState {
  runOneTimeAuthStorageReset();

  const localDevAuthState = buildLocalDevAuthState();
  if (localDevAuthState !== null) return { ...localDevAuthState, isBootstrapped: false };

  const rawAccess = readLocalStorage(LS_KEYS.ACCESS_TOKEN);
  const rawRefresh = readLocalStorage(LS_KEYS.REFRESH_TOKEN);
  const rawEmail = readLocalStorage(LS_KEYS.EMAIL);
  const rawAccount = readLocalStorage(LS_KEYS.SELECTED_ACCOUNT);

  const accessToken = isAuthTokenMeta(rawAccess) ? rawAccess : null;
  const refreshToken = isAuthTokenMeta(rawRefresh) ? rawRefresh : null;
  const email = typeof rawEmail === "string" ? rawEmail : null;
  const selectedAccount = isSelectedAccount(rawAccount) ? rawAccount : null;

  // Purge any entries that were structurally corrupt so they don't persist.
  if (accessToken === null && rawAccess !== null) removeLocalStorage(LS_KEYS.ACCESS_TOKEN);
  if (refreshToken === null && rawRefresh !== null) removeLocalStorage(LS_KEYS.REFRESH_TOKEN);
  if (email === null && rawEmail !== null) removeLocalStorage(LS_KEYS.EMAIL);
  if (selectedAccount === null && rawAccount !== null) removeLocalStorage(LS_KEYS.SELECTED_ACCOUNT);

  return { accessToken, refreshToken, email, selectedAccount, isBootstrapped: false };
}

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [state, setState] = React.useState<AuthState>(EMPTY_AUTH_STATE);

  // Read browser-only persisted auth after hydration so the first client render
  // matches Gatsby's static HTML.
  React.useEffect(() => {
    setState({ ...buildInitialState(), isBootstrapped: true });
  }, []);

  const value = React.useMemo<AuthContextValue>(
    () => ({ state, setState }),
    [state, setState]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useAuthContext(): AuthContextValue {
  const ctx = React.useContext(AuthContext);
  if (ctx === undefined) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return ctx;
}

export { AuthContext };
